/** A menu pane that owns vertical overflow, rather than an ancestor or the page. */
export function scrollsVertically(el: HTMLElement): boolean {
  if (el.scrollHeight <= el.clientHeight + 1) return false;
  const overflow = getComputedStyle(el).overflowY;
  return overflow === "auto" || overflow === "scroll";
}

/**
 * Native panning does not reliably follow the portrait fallback's rotated page. Delegate one
 * gesture to the nearest overflowing pane; normal landscape keeps the browser's native scroll.
 * The returned cancellation belongs to the screen's hide lifecycle.
 */
export function setupMenuScroll(root: HTMLElement, isOpen: () => boolean): () => void {
  const dragThreshold = 8;
  let swipe: {
    id: number;
    x: number;
    y: number;
    top: number;
    dragged: boolean;
    pane: HTMLElement;
  } | null = null;
  let draggedClick: number | null = null;
  const cancel = (): void => {
    const previous = swipe;
    swipe = null;
    draggedClick = null;
    if (previous?.pane.hasPointerCapture(previous.id)) previous.pane.releasePointerCapture(previous.id);
  };
  root.addEventListener("pointerdown", (e) => {
    if (!e.isPrimary || swipe) return;
    draggedClick = null;
    if (e.pointerType !== "touch" || !isOpen()) return;
    if (!document.documentElement.classList.contains("landscape-fallback")) return;
    const target = e.target;
    if (!(target instanceof Node)) return;
    let pane: HTMLElement | null = target instanceof HTMLElement ? target : target.parentElement;
    while (pane && root.contains(pane) && !scrollsVertically(pane)) {
      pane = pane === root ? null : pane.parentElement;
    }
    if (!pane) return;
    swipe = { id: e.pointerId, x: e.clientX, y: e.clientY, top: pane.scrollTop, dragged: false, pane };
  });
  root.addEventListener("pointermove", (e) => {
    if (!swipe || swipe.id !== e.pointerId) return;
    const delta = e.clientX - swipe.x;
    if (!swipe.dragged) {
      if (Math.max(Math.abs(delta), Math.abs(e.clientY - swipe.y)) < dragThreshold) return;
      swipe.dragged = true;
      swipe.pane.setPointerCapture(e.pointerId);
    }
    e.preventDefault();
    // In the 90-degree fallback, moving right moves upward through the logical pane.
    swipe.pane.scrollTop = swipe.top + delta;
  });
  root.addEventListener("pointerup", (e) => {
    if (!swipe || swipe.id !== e.pointerId) return;
    if (swipe.dragged) {
      e.preventDefault();
      draggedClick = e.pointerId;
    }
    swipe = null;
  });
  root.addEventListener("pointercancel", (e) => {
    if (swipe?.id === e.pointerId) cancel();
  });
  root.addEventListener("lostpointercapture", (e) => {
    // Taking capture from a pressed button first emits that button's bubbling lost-capture event.
    if (swipe?.id === e.pointerId && e.target === swipe.pane) cancel();
  });
  root.addEventListener(
    "click",
    (e) => {
      if (draggedClick === null) return;
      if ((e as PointerEvent).pointerId === draggedClick || e.detail > 0) {
        // Action handlers honor defaultPrevented; keep bubbling for the native-click input handoff.
        e.preventDefault();
        draggedClick = null;
      }
    },
    { capture: true },
  );
  window.addEventListener("blur", cancel);
  window.addEventListener("lacheviewportchange", cancel);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) cancel();
  });
  return cancel;
}

/** Reveal a row without scrolling its neighboring content, header, or the page, even when rotated. */
export function revealMenuRow(row: HTMLElement, pane: HTMLElement): void {
  let top = 0;
  let el: HTMLElement | null = row;
  while (el && el !== pane) {
    top += el.offsetTop;
    el = el.offsetParent as HTMLElement | null;
  }
  if (el !== pane) return;
  const bottom = top + row.offsetHeight;
  if (top < pane.scrollTop) pane.scrollTop = top;
  else if (bottom > pane.scrollTop + pane.clientHeight) pane.scrollTop = bottom - pane.clientHeight;
}
